<template>
  <v-container fluid>
    <v-card style="border-radius: 20px;">
      <v-row>
        <div class="title-displays" style="padding-top: 15px; padding-left: 15px">
          Propostas
        </div>
        <v-spacer></v-spacer>
        <div class="d-flex align-center" style="gap: 16px; padding-top: 15px; padding-right: 15px">
          <v-btn
            color="primary"
            text
            @click="loadProposals"
            :disabled="is_loading"
            :loading="is_loading"
          >
            <v-icon left>mdi-refresh</v-icon>
            Atualizar
          </v-btn>
        </div>
      </v-row>

      <v-divider></v-divider>

      <!-- Filtros -->
      <v-row dense class="ma-2">
        <v-col cols="12" md="4">
          <v-text-field
            prepend-icon="mdi-magnify"
            label="Buscar"
            v-model="searchField"
            outlined
            dense
            clearable
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="searchOptionSelected"
            :items="searchOptions"
            label="Buscar em"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            label="Status"
            outlined
            dense
            clearable
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="originFilter"
            :items="originOptions"
            label="Origem"
            outlined
            dense
            clearable
          />
        </v-col>
      </v-row>

      <!-- Filtros de Data -->
      <v-row dense class="ma-2">
        <v-col cols="12" md="4">
          <v-menu
            v-model="menuDateStart"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dateStartFormatted"
                label="Data Inicial"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                outlined
                dense
                clearable
                @click:clear="dateStart = null"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dateStart"
              locale="pt-br"
              @input="menuDateStart = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="4">
          <v-menu
            v-model="menuDateEnd"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dateEndFormatted"
                label="Data Final"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                outlined
                dense
                clearable
                @click:clear="dateEnd = null"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dateEnd"
              locale="pt-br"
              @input="menuDateEnd = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="4">
          <v-btn
            color="secondary"
            text
            @click="clearDateFilters"
            :disabled="!dateStart && !dateEnd"
          >
            <v-icon left>mdi-filter-remove</v-icon>
            Limpar Filtros de Data
          </v-btn>
        </v-col>
      </v-row>

      <!-- Tabela de Propostas -->
      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="filteredHeaders"
            :items="getItems"
            :loading="is_loading"
            no-data-text="Sem propostas para exibir"
            style="border-radius: 20px"
            multi-sort
          >
            <template v-slot:item.id_doc="{ item }">
              <span class="text-caption">{{ item.id_doc || 'N/A' }}</span>
            </template>

            <template v-slot:item.proposalType="{ item }">
              <v-chip small :color="getOriginColor(item.proposalSource)" text-color="white">
                {{ item.proposalType }}
              </v-chip>
            </template>

            <template v-slot:item.statusFormatted="{ item }">
              <v-chip
                small
                :color="getStatusColor(item.status)"
                text-color="white"
              >
                {{ item.statusFormatted }}
              </v-chip>
            </template>

            <template v-slot:item.totalInvestment="{ item }">
              {{ item.totalInvestment }}
            </template>

            <template v-slot:item.totalProductsQuantity="{ item }">
              <span class="font-weight-medium">{{ item.totalProductsQuantity || 0 }}</span>
            </template>

            <template v-slot:item.totalServicesQuantity="{ item }">
              <span class="font-weight-medium">{{ item.totalServicesQuantity || 0 }}</span>
            </template>

            <template v-slot:item.createdAtFormatted="{ item }">
              {{ item.createdAtFormatted }}
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center" style="gap: 8px;">
                <v-btn
                  small
                  color="primary"
                  text
                  @click="viewProposal(item)"
                  :disabled="is_loading"
                >
                  <v-icon small>mdi-eye</v-icon>
                  Visualizar
                </v-btn>
                <v-btn
                  v-if="canAddDiscount(item)"
                  small
                  color="success"
                  text
                  @click="openDiscountModal(item)"
                  :disabled="is_loading"
                >
                  <v-icon small>mdi-tag-outline</v-icon>
                  Adicionar desconto
                </v-btn>
                <v-btn
                  v-if="item.billingOrderId"
                  small
                  color="purple"
                  text
                  @click="viewBillingOrder(item)"
                  :disabled="is_loading"
                >
                  <v-icon small>mdi-receipt</v-icon>
                  Ordem
                </v-btn>
                <v-btn
                  v-if="item.status === 'billing_requested' && !isFranchisee"
                  small
                  color="success"
                  text
                  @click="markAsBilled(item)"
                  :disabled="is_loading"
                >
                  <v-icon small>mdi-check-circle</v-icon>
                  Marcar Faturada
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>

    <!-- Modal de Visualização Simplificada da Proposta -->
    <v-dialog
      v-model="showProposalModal"
      max-width="800px"
      scrollable
    >
      <v-card v-if="selectedProposal">
        <v-card-title class="headline primary white--text">
          <v-icon left color="white">mdi-file-document-outline</v-icon>
          Visualização da Proposta
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Informações Principais -->
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">ID da Proposta</div>
                <div class="info-value">{{ selectedProposal.id_doc || 'N/A' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">Origem</div>
                <div class="info-value">
                  <v-chip small :color="getOriginColor(selectedProposal.proposalSource)" text-color="white">
                    {{ selectedProposal.proposalType }}
                  </v-chip>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">Cliente</div>
                <div class="info-value">{{ selectedProposal.leadName || 'N/A' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">Documento</div>
                <div class="info-value">{{ selectedProposal.leadDocument || 'N/A' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6" v-if="!isFranchisee">
              <div class="info-item mb-3">
                <div class="info-label">Franqueado</div>
                <div class="info-value">{{ selectedProposal.franchiseeName || 'N/A' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">Tipo de Proposta</div>
                <div class="info-value">{{ selectedProposal.typeProposalText || 'N/A' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">Status</div>
                <div class="info-value">
                  <v-chip
                    small
                    :color="getStatusColor(selectedProposal.status)"
                    text-color="white"
                  >
                    {{ selectedProposal.statusFormatted }}
                  </v-chip>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">Data de Criação</div>
                <div class="info-value">{{ selectedProposal.createdAtFormatted || 'N/A' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">Quantidade de Produtos</div>
                <div class="info-value font-weight-bold">{{ selectedProposal.totalProductsQuantity || 0 }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">Quantidade de Serviços</div>
                <div class="info-value font-weight-bold">{{ selectedProposal.totalServicesQuantity || 0 }}</div>
              </div>
            </v-col>
            <v-col cols="12">
              <div class="info-item mb-3" v-if="selectedProposal.discountAmountValue > 0">
                <div class="info-label">Subtotal</div>
                <div class="info-value">
                  {{ formatCurrency(selectedProposal.subtotalInvestment) }}
                </div>
              </div>
              <div class="info-item mb-3" v-if="selectedProposal.discountAmountValue > 0">
                <div class="info-label">Desconto</div>
                <div class="info-value success--text font-weight-bold">
                  - {{ formatCurrency(selectedProposal.discountAmountValue) }}
                </div>
              </div>
              <div class="info-item mb-3">
                <div class="info-label">Valor Total</div>
                <div class="info-value font-weight-bold text-h6 primary--text">
                  {{ selectedProposal.totalInvestment || 'R$ 0,00' }}
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Lista de Itens (Opcional) -->
          <v-divider class="my-4"></v-divider>
          <div v-if="getProposalItems(selectedProposal).length > 0">
            <div class="text-h6 mb-3">Itens da Proposta</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Nome</th>
                  <th class="text-right">Quantidade</th>
                  <th class="text-right">Valor Unitário</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in getProposalItems(selectedProposal)" :key="index">
                  <td>
                    <v-chip x-small :color="item.type === 'Solução' ? 'purple' : item.type === 'Produto' ? 'blue' : 'green'" text-color="white">
                      {{ item.type }}
                    </v-chip>
                  </td>
                  <td>{{ item.name }}</td>
                  <td class="text-right">{{ item.quantity }}</td>
                  <td class="text-right">{{ item.unitPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</td>
                  <td class="text-right font-weight-medium">{{ item.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeProposalModal">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Desconto -->
    <v-dialog
      v-model="showDiscountModal"
      max-width="560px"
      persistent
    >
      <v-card v-if="selectedDiscountProposal">
        <v-card-title class="headline primary white--text">
          <v-icon left color="white">mdi-tag-outline</v-icon>
          Adicionar desconto
        </v-card-title>

        <v-card-text class="pa-4">
          <v-form ref="discountForm" v-model="discountFormValid" lazy-validation>
            <v-row dense>
              <v-col cols="12">
                <div class="info-item mb-3">
                  <div class="info-label">Proposta</div>
                  <div class="info-value">{{ selectedDiscountProposal.id_doc || 'N/A' }}</div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="info-item mb-3">
                  <div class="info-label">Subtotal</div>
                  <div class="info-value font-weight-bold">
                    {{ discountSubtotalFormatted }}
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="info-item mb-3">
                  <div class="info-label">Total com desconto</div>
                  <div class="info-value font-weight-bold primary--text">
                    {{ discountFinalTotalFormatted }}
                  </div>
                </div>
              </v-col>

              <v-col cols="12">
                <v-switch
                  v-model="discountForm.hasDiscount"
                  inset
                  label="Aplicar desconto"
                />
              </v-col>

              <v-col cols="12" v-if="discountForm.hasDiscount">
                <v-radio-group
                  v-model="discountForm.discountType"
                  row
                  label="Tipo de desconto"
                >
                  <v-radio label="Valor fixo" value="monetary" />
                  <v-radio label="Porcentagem" value="percentage" />
                </v-radio-group>
              </v-col>

              <v-col cols="12" md="6" v-if="discountForm.hasDiscount">
                <v-text-field
                  v-model.number="discountForm.discountValue"
                  outlined
                  dense
                  type="number"
                  min="0"
                  label="Desconto"
                  :prefix="discountForm.discountType === 'monetary' ? 'R$' : ''"
                  :suffix="discountForm.discountType === 'percentage' ? '%' : ''"
                  :rules="discountValueRules"
                />
              </v-col>

              <v-col cols="12" md="6" v-if="discountForm.hasDiscount">
                <div class="info-item mb-3">
                  <div class="info-label">Valor do desconto</div>
                  <div class="info-value success--text font-weight-bold">
                    {{ discountAmountFormatted }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDiscountModal" :disabled="is_loading">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="saveDiscount" :loading="is_loading">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Billing Order View Dialog -->
    <BillingOrderViewDialog
      :openModal="openBillingOrderViewDialog"
      :billingOrderId="selectedBillingOrderId"
      @close="closeBillingOrderViewDialog"
    />
  </v-container>
</template>

<script>
import ProposalsScript from "./ProposalsScript.js";
import BillingOrderViewDialog from "@/components/ProjectsKits/BillingOrder/BillingOrderViewDialog.vue";

export default {
  extends: ProposalsScript,
  components: {
    BillingOrderViewDialog,
  },
  computed: {
    ...ProposalsScript.computed,
  },
  methods: {
    ...ProposalsScript.methods,
    getStatusColor(status) {
      const colorMap = {
        'pending': 'orange',
        'accepted': 'green',
        'billing_requested': 'orange',
        'billed': 'blue',
      };
      return colorMap[status] || 'grey';
    },
    getOriginColor(source) {
      const colorMap = {
        'diagnostic_proposals': 'purple',
        'diagnostics': 'indigo',
        'water_scan': 'cyan',
        'water_fix': 'teal',
      };
      return colorMap[source] || 'grey';
    },
  },
};
</script>

<style scoped>
.title-displays {
  font-size: 25px;
  padding-top: 8px;
  padding-bottom: 8px;
  font-weight: 500;
}

.info-item {
  padding: 8px;
  border-radius: 4px;
  background-color: #f5f5f5;
}

.info-label {
  font-size: 12px;
  color: #757575;
  text-transform: uppercase;
  font-weight: 500;
  margin-bottom: 4px;
}

.info-value {
  font-size: 14px;
  color: #212121;
}
</style>

